class CartUpsells {
  constructor() {
    this.container = document.querySelector('[data-cart-upsells]');
    this.grid = document.querySelector('[data-cart-upsells-grid]');

    if (!this.container || !this.grid) {
      return;
    }

    this.maxProducts = 3;
    this.maxRecommendationsPerProduct = 10;
    this.largeCartThreshold = 6;

    this.init();
  }

  async init() {
    try {
      const cart = await this.getCart();

      if (!cart.items || cart.items.length === 0) {
        this.hide();
        return;
      }

      await this.buildUpsells(cart);

    } catch (error) {
      console.error('[Cart Upsells]', error);
      this.hide();
    }
  }

  /**
   * Get the current Shopify cart.
   */
  async getCart() {
    const response = await fetch(
      `${window.Shopify.routes.root}cart.js`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error('Unable to load cart.');
    }

    return response.json();
  }

  /**
   * Build the upsell list.
   *
   * < 6 products:
   *     Use recommendations from the FIRST cart product.
   *
   * 6+ products:
   *     Use recommendations from ALL cart products.
   *
   * In both cases:
   *     - Remove products already in cart.
   *     - Remove duplicates.
   *     - Show maximum 3.
   */
  async buildUpsells(cart) {

    /*
     * Product IDs already in the cart.
     */
    const cartProductIds = new Set(
      cart.items.map(item => String(item.product_id))
    );

    /*
     * Don't request recommendations twice
     * if the same product has multiple variants
     * in the cart.
     */
    const uniqueCartItems = [];
    const seenCartProductIds = new Set();

    cart.items.forEach(item => {

      const productId = String(item.product_id);

      if (!seenCartProductIds.has(productId)) {

        seenCartProductIds.add(productId);

        uniqueCartItems.push(item);
      }
    });


    /*
     * CLIENT RULE:
     *
     * 1–5 products:
     *     Use the first cart product's recommendations.
     *
     * 6+ products:
     *     Use recommendations from ALL products.
     */
    let sourceItems;

    if (cart.item_count < this.largeCartThreshold) {

      sourceItems = uniqueCartItems.slice(0, 1);

    } else {

      sourceItems = uniqueCartItems;

    }


    /*
     * Get recommendations.
     */
    const recommendationResults = await Promise.all(
      sourceItems.map(item =>
        this.getRecommendations(item.product_id)
      )
    );


    /*
     * Convert recommendations into candidates.
     */
    const candidates = [];

    recommendationResults.forEach(
      (recommendations, sourceIndex) => {

        const sourceItem = sourceItems[sourceIndex];

        recommendations.forEach(
          (product, recommendationIndex) => {

            const productId = String(product.id);

            /*
             * NEVER show something already in cart.
             */
            if (cartProductIds.has(productId)) {
              return;
            }

            candidates.push({
              product,
              sourceProductId: String(sourceItem.product_id),
              position: recommendationIndex + 1
            });

          }
        );

      }
    );


    /*
     * For carts below 6:
     *
     * Keep Shopify's recommendation order.
     *
     * This means:
     *
     * Product A
     *   ↓
     * Recommendation 1
     * Recommendation 2
     * Recommendation 3
     * Recommendation 4
     *
     * If Recommendation 2 is already in cart,
     * Recommendation 4 can take its place.
     */
    let finalProducts;


    if (cart.item_count < this.largeCartThreshold) {

      finalProducts =
        this.removeDuplicatesPreserveOrder(candidates)
          .slice(0, this.maxProducts);

    } else {

      /*
       * For 6+ products:
       *
       * Rank products by how many different
       * cart products recommend them.
       *
       * If:
       *
       * A → X
       * B → X
       * C → X
       *
       * X gets a strong score.
       */
      finalProducts =
        this.rankLargeCartRecommendations(candidates)
          .slice(0, this.maxProducts);

    }


    if (!finalProducts.length) {
      this.hide();
      return;
    }


    this.render(finalProducts);
  }


  /**
   * Shopify Product Recommendations API.
   */
  async getRecommendations(productId) {

    const url =
      `${window.Shopify.routes.root}` +
      `recommendations/products.json` +
      `?product_id=${encodeURIComponent(productId)}` +
      `&limit=${this.maxRecommendationsPerProduct}` +
      `&intent=related`;


    try {

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json'
        }
      });


      if (!response.ok) {
        return [];
      }


      const data = await response.json();


      if (!data || !Array.isArray(data.products)) {
        return [];
      }


      return data.products;

    } catch (error) {

      console.error(
        '[Cart Upsells] Recommendation request failed:',
        error
      );

      return [];
    }
  }


  /**
   * Remove duplicate products but preserve
   * Shopify's original recommendation order.
   */
  removeDuplicatesPreserveOrder(candidates) {

    const seen = new Set();
    const products = [];

    candidates.forEach(candidate => {

      const productId =
        String(candidate.product.id);

      if (seen.has(productId)) {
        return;
      }

      seen.add(productId);

      products.push(candidate.product);
    });

    return products;
  }


  /**
   * Ranking for carts with 6+ products.
   */
  rankLargeCartRecommendations(candidates) {

    const scores = new Map();


    candidates.forEach(candidate => {

      const productId =
        String(candidate.product.id);


      if (!scores.has(productId)) {

        scores.set(productId, {
          product: candidate.product,
          score: 0,
          sources: new Set(),
          bestPosition: candidate.position
        });

      }


      const entry =
        scores.get(productId);


      /*
       * If different cart products recommend
       * the same product, give it a strong bonus.
       */
      if (!entry.sources.has(
        candidate.sourceProductId
      )) {

        entry.sources.add(
          candidate.sourceProductId
        );

        entry.score += 100;
      }


      /*
       * Better Shopify recommendation position
       * gets a slightly higher score.
       *
       * Position 1 = +50
       * Position 2 = +49
       * ...
       */
      entry.score += Math.max(
        1,
        51 - candidate.position
      );


      entry.bestPosition =
        Math.min(
          entry.bestPosition,
          candidate.position
        );

    });


    return Array.from(scores.values())

      .sort((a, b) => {

        /*
         * First priority:
         * overall score.
         */
        if (b.score !== a.score) {
          return b.score - a.score;
        }


        /*
         * Second priority:
         * better recommendation position.
         */
        return a.bestPosition -
          b.bestPosition;

      })

      .map(entry => entry.product);
  }


  /**
   * Render the three products.
   */
  render(products) {

    this.grid.innerHTML =
      products.map(product => {

        const variant =
          this.getAvailableVariant(product);


        /*
         * Don't show products without an
         * available variant.
         */
        if (!variant) {
          return '';
        }


        const image =
          product.featured_image ||
          (
            product.images &&
            product.images.length
              ? product.images[0]
              : ''
          );


        const price =
          this.formatMoney(product.price);


        return `
          <article
            class="cart-upsells__product"
            data-product-id="${product.id}"
          >

            <a
              href="${this.escapeAttribute(product.url)}"
              class="cart-upsells__image"
            >

              ${
                image
                  ? `
                    <img
                      src="${this.escapeAttribute(image)}"
                      alt="${this.escapeAttribute(product.title)}"
                      loading="lazy"
                      width="600"
                      height="600"
                    >
                  `
                  : ''
              }

            </a>


            <div class="cart-upsells__content">

              <a
                href="${this.escapeAttribute(product.url)}"
                class="cart-upsells__product-title"
              >
                ${this.escapeHtml(product.title)}
              </a>


              <div class="cart-upsells__price">
                ${price}
              </div>


              <button
                type="button"
                class="cart-upsells__add btn btn--primary"
                data-upsell-variant-id="${variant.id}"
              >
                <span class="btn__text">
+ Toevoegen
                </span>
              </button>

            </div>

          </article>
        `;

      }).join('');


    this.container.classList.add(
      'cart-upsells--visible'
    );


    this.bindAddButtons();
  }


  /**
   * Get first available variant.
   */
  getAvailableVariant(product) {

    if (
      !product.variants ||
      !product.variants.length
    ) {
      return null;
    }


    return product.variants.find(
      variant => variant.available
    ) || null;
  }


  /**
   * Add upsell to Shopify cart.
   */
  bindAddButtons() {

    const buttons =
      this.grid.querySelectorAll(
        '[data-upsell-variant-id]'
      );


    buttons.forEach(button => {

      button.addEventListener(
        'click',
        () => this.addToCart(button)
      );

    });
  }


  async addToCart(button) {

    const variantId =
      Number(
        button.dataset.upsellVariantId
      );


    if (!variantId) {
      return;
    }


    const textElement =
      button.querySelector('.btn__text');


    const originalText =
      textElement
        ? textElement.textContent
        : button.textContent;


    button.disabled = true;


    if (textElement) {
      textElement.textContent = 'Toevoegen...';
    } else {
      button.textContent = 'Toevoegen...';
    }


    try {

      const response =
        await fetch(
          `${window.Shopify.routes.root}cart/add.js`,
          {
            method: 'POST',

            headers: {
              'Content-Type':
                'application/json',

              'Accept':
                'application/json'
            },

            body: JSON.stringify({

              items: [
                {
                  id: variantId,
                  quantity: 1
                }
              ]

            })
          }
        );


      if (!response.ok) {

        const errorData =
          await response.json()
            .catch(() => null);

        throw new Error(
          errorData?.description ||
          'Unable to add product.'
        );
      }


      /*
       * IMPORTANT:
       *
       * We reload the cart page here instead of
       * trying to manipulate this theme's
       * <main-cart> internals.
       *
       * The theme already has its own cart.js
       * responsible for cart rendering.
       *
       * After reload:
       * - cart is updated
       * - cart count is updated
       * - upsells are recalculated
       * - products already in cart disappear
       */
      window.location.reload();


    } catch (error) {

      console.error(
        '[Cart Upsells] Add failed:',
        error
      );


      button.disabled = false;


      if (textElement) {
        textElement.textContent =
          originalText;
      } else {
        button.textContent =
          originalText;
      }

    }
  }


  /**
   * Shopify money formatting.
   */
  formatMoney(price) {
  const amount = Number(price) / 100;

  return '€' + amount.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}


  escapeHtml(value) {

    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }


  escapeAttribute(value) {

    return this.escapeHtml(value);
  }


  hide() {

    this.container.classList.remove(
      'cart-upsells--visible'
    );

    this.container.style.display = 'none';
  }
}


/*
 * Start when DOM is ready.
 */
if (
  document.readyState === 'loading'
) {

  document.addEventListener(
    'DOMContentLoaded',
    () => new CartUpsells()
  );

} else {

  new CartUpsells();

}